import { z } from 'zod';
import { RecordIdSchema } from './identifiers';
import { LocaleSchema } from './localized';
import { MoneySchema } from './money';
import { BusinessDateSchema, InstantSchema } from './time';

/**
 * Manual WhatsApp reminders (COL-REMIND, owner-approved manual mode — ADR-0043).
 *
 * A collector prepares a reminder in the system, previews the exact text, and opens WhatsApp through
 * the **official click-to-chat link** (`https://wa.me/<number>?text=…`) — or copies the text and the
 * number. The person presses Send in WhatsApp themselves. Nothing here sends a message, automates a
 * browser or reads a WhatsApp session.
 *
 * What is recorded is therefore what a person **did or said they did**, never a delivery:
 *
 * - `prepared` — the system built the text and the link.
 * - `linkOpened` / `copied` — the person opened the link or copied the text or number. Opening the
 *   link is **not** proof that anything was sent.
 * - `sentManually` — the person explicitly confirmed they pressed Send. Self-reported, stored with who
 *   and when; never "delivered" or "read", which only a connected provider could know.
 * - `contacted`, `skipped`, `retryLater` — other outcomes a collector records.
 *
 * Automatic sending is a separate, optional integration that waits for a provider choice and its
 * cost approval; until one is configured the status says so and no worker sends anything.
 */

/* ------------------------------------------------------------------ templates */

/** The only values a reminder text may contain. Anything else in braces is refused. */
export const REMINDER_TEMPLATE_VARIABLES = [
  'customerName',
  'companyName',
  'contractNumber',
  'amount',
  'dueDate',
] as const;
export type ReminderTemplateVariable = (typeof REMINDER_TEMPLATE_VARIABLES)[number];

const PLACEHOLDER = /\{\{\s*([^{}]*?)\s*\}\}/g;

/** The variables a template names, in order of appearance (duplicates kept). */
export function templateVariables(template: string): string[] {
  return [...template.matchAll(PLACEHOLDER)].map((match) => match[1] ?? '');
}

/**
 * A template is valid when every `{{…}}` names an allowed variable and no stray brace is left — a
 * single `{` or `}` is almost always a broken placeholder that would reach the customer verbatim.
 */
export function isValidReminderTemplate(template: string): boolean {
  if (template.trim().length === 0 || template.length > 1000) return false;
  const names = templateVariables(template);
  if (names.some((name) => !(REMINDER_TEMPLATE_VARIABLES as readonly string[]).includes(name))) {
    return false;
  }
  return !/[{}]/.test(template.replace(PLACEHOLDER, ''));
}

export const ReminderTemplateSchema = z
  .string()
  .max(1000)
  .refine(isValidReminderTemplate, { message: 'TEMPLATE_VARIABLE_NOT_ALLOWED' });

export const ReminderTemplatesSchema = z.strictObject({
  ar: ReminderTemplateSchema,
  en: ReminderTemplateSchema,
});
export type ReminderTemplates = z.infer<typeof ReminderTemplatesSchema>;

/**
 * The system-provided wording, used until an administrator configures another (the owner accepted
 * system-provided layouts). It names the company from the runtime identity, never a fixed brand.
 */
export const DEFAULT_REMINDER_TEMPLATES: ReminderTemplates = {
  ar:
    'السيد/السيدة {{customerName}}، تحية طيبة من {{companyName}}. نذكّركم بموعد استحقاق قسط ' +
    'بقيمة {{amount}} بتاريخ {{dueDate}} ضمن العقد رقم {{contractNumber}}. إذا كنتم قد سددتم ' +
    'القسط فنرجو تجاهل هذه الرسالة. شكرًا لكم.',
  en:
    'Dear {{customerName}}, greetings from {{companyName}}. This is a reminder that an instalment ' +
    'of {{amount}} is due on {{dueDate}} under contract {{contractNumber}}. If you have already ' +
    'paid, please disregard this message. Thank you.',
};

/** Fill a valid template. Values are inserted as text: nothing in a value is treated as a placeholder. */
export function renderReminderTemplate(
  template: string,
  values: Readonly<Record<ReminderTemplateVariable, string>>,
): string {
  return template.replace(PLACEHOLDER, (_whole, name: string) =>
    (REMINDER_TEMPLATE_VARIABLES as readonly string[]).includes(name)
      ? values[name as ReminderTemplateVariable]
      : '',
  );
}

/* ------------------------------------------------------------- phone numbers */

/**
 * International calling codes for the countries a deployment is likely to be configured with. A
 * local number (`01…`) is completed with the company country's code; a country not listed here
 * requires numbers entered with their code (`+…`).
 */
export const CALLING_CODES: Readonly<Record<string, string>> = {
  EG: '20',
  SA: '966',
  AE: '971',
  KW: '965',
  QA: '974',
  BH: '973',
  OM: '968',
  JO: '962',
  LB: '961',
  IQ: '964',
  MA: '212',
  TN: '216',
  DZ: '213',
  LY: '218',
  SD: '249',
  US: '1',
  GB: '44',
};

export type WhatsAppNumberResult =
  | { ok: true; digits: string }
  | { ok: false; code: 'PHONE_MISSING' | 'PHONE_INVALID' | 'PHONE_COUNTRY_CODE_REQUIRED' };

/**
 * A stored phone as the digits WhatsApp expects: country code first, no `+`, no trunk `0`, 8–15
 * digits (E.164). `+20 100…` and `0020 100…` keep their code; `0100…` takes the company country's
 * code without its leading `0`; anything else without a code is refused rather than guessed.
 */
export function normalizeWhatsAppNumber(
  raw: string | undefined,
  defaultCallingCode: string | undefined,
): WhatsAppNumberResult {
  const text = (raw ?? '').trim();
  if (!text) return { ok: false, code: 'PHONE_MISSING' };
  if (!/^\+?[0-9\s().-]+$/.test(text)) return { ok: false, code: 'PHONE_INVALID' };
  const digits = text.replace(/[^0-9]/g, '');
  let international: string;
  if (text.startsWith('+')) international = digits;
  else if (digits.startsWith('00')) international = digits.slice(2);
  else if (digits.startsWith('0')) {
    if (!defaultCallingCode) return { ok: false, code: 'PHONE_COUNTRY_CODE_REQUIRED' };
    international = `${defaultCallingCode}${digits.slice(1)}`;
  } else if (defaultCallingCode && digits.startsWith(defaultCallingCode)) international = digits;
  else return { ok: false, code: 'PHONE_COUNTRY_CODE_REQUIRED' };
  if (!/^[1-9][0-9]{7,14}$/.test(international)) return { ok: false, code: 'PHONE_INVALID' };
  return { ok: true, digits: international };
}

/** The last four digits, for lists and history. The full number appears only in a prepared link. */
export function maskPhoneDigits(digits: string): string {
  return `••••${digits.slice(-4)}`;
}

/** The official click-to-chat link, with the text encoded so every character survives. */
export function whatsAppClickToChatUrl(digits: string, text: string): string {
  return `https://wa.me/${digits}?text=${encodeURIComponent(text)}`;
}

/* ---------------------------------------------------------------- the queue */

export const CONTACT_BUCKETS = ['upcoming', 'overdue'] as const;
export const ContactBucketSchema = z.enum(CONTACT_BUCKETS);
export type ContactBucket = z.infer<typeof ContactBucketSchema>;

/** What a person did, or said they did. See the file comment: none of these is a delivery. */
export const CONTACT_OUTCOMES = [
  'prepared',
  'linkOpened',
  'copied',
  'sentManually',
  'contacted',
  'skipped',
  'retryLater',
] as const;
export const ContactOutcomeSchema = z.enum(CONTACT_OUTCOMES);
export type ContactOutcome = z.infer<typeof ContactOutcomeSchema>;

/** Outcomes a person records after preparing. `prepared` is the system's own. */
export const RECORDABLE_CONTACT_OUTCOMES = [
  'linkOpened',
  'copied',
  'sentManually',
  'contacted',
  'skipped',
  'retryLater',
] as const;

/** Outcomes that mean the customer was (by the person's own account) reached about this due date. */
export const REACHED_OUTCOMES: readonly ContactOutcome[] = ['sentManually', 'contacted'];

/** Where an item stands, from its latest recorded outcome; `due` when nothing was recorded yet. */
export const CONTACT_ITEM_STATES = ['due', ...CONTACT_OUTCOMES] as const;
export const ContactItemStateSchema = z.enum(CONTACT_ITEM_STATES);
export type ContactItemState = z.infer<typeof ContactItemStateSchema>;

export const PHONE_STATUSES = ['valid', 'missing', 'invalid', 'needsCountryCode'] as const;
export const CONSENT_STATUSES = ['granted', 'withdrawn', 'notRecorded'] as const;

export const ContactQueueItemSchema = z.strictObject({
  installmentId: RecordIdSchema,
  sequence: z.number().int().positive(),
  contractId: RecordIdSchema,
  contractNumber: z.string().max(80),
  customerId: RecordIdSchema,
  customerName: z.string().max(200),
  unitCode: z.string().max(80).optional(),
  dueOn: BusinessDateSchema,
  amountDue: MoneySchema,
  bucket: ContactBucketSchema,
  /** Negative when overdue: the days since the due date. */
  daysUntilDue: z.number().int(),
  /** Upcoming only: true once a configured reminder window (15 days by default) has opened. */
  reminderDue: z.boolean(),
  phone: z.strictObject({
    status: z.enum(PHONE_STATUSES),
    masked: z.string().max(20).optional(),
  }),
  consent: z.enum(CONSENT_STATUSES),
  preferredLanguage: LocaleSchema.optional(),
  state: ContactItemStateSchema,
  lastContact: z
    .strictObject({
      outcome: ContactOutcomeSchema,
      at: InstantSchema,
      byAccountId: z.string().min(1).max(200),
      selfReported: z.boolean(),
    })
    .optional(),
  /** By the person's own account, the customer was reached about this due date in this bucket. */
  reachedInBucket: z.boolean(),
  retryOn: BusinessDateSchema.optional(),
});
export type ContactQueueItem = z.infer<typeof ContactQueueItemSchema>;

export const ContactQueueQuerySchema = z.strictObject({
  bucket: ContactBucketSchema.default('upcoming'),
  contractId: RecordIdSchema.optional(),
  customerId: RecordIdSchema.optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  cursor: z.string().min(1).max(200).optional(),
});
export type ContactQueueQuery = z.infer<typeof ContactQueueQuerySchema>;

export const ContactQueuePageSchema = z.strictObject({
  items: z.array(ContactQueueItemSchema),
  nextCursor: z.string().optional(),
  /** The windows used for "reminder due", from `collections.reminderWindowsDays`. */
  windowsDays: z.array(z.number().int()),
  today: BusinessDateSchema,
  /** Always `manual` until a provider is configured (see `ContactStatusSchema`). */
  mode: z.literal('manual'),
});
export type ContactQueuePage = z.infer<typeof ContactQueuePageSchema>;

const ReasonSchema = z.string().trim().min(3).max(500);

export const PrepareContactSchema = z
  .strictObject({
    installmentId: RecordIdSchema,
    language: LocaleSchema,
    phone: z.enum(['primary', 'secondary']).default('primary'),
    /** An explicit follow-up after the customer was already reached about this due date. */
    followUp: z.boolean().default(false),
    reason: ReasonSchema.optional(),
    idempotencyKey: z.string().min(8).max(100),
  })
  .refine((value) => !value.followUp || value.reason !== undefined, {
    message: 'FOLLOW_UP_REASON_REQUIRED',
    path: ['reason'],
  });
export type PrepareContact = z.infer<typeof PrepareContactSchema>;

export const PreparedContactSchema = z.strictObject({
  attemptId: RecordIdSchema,
  installmentId: RecordIdSchema,
  language: LocaleSchema,
  /** The exact text the link carries. */
  message: z.string().max(2000),
  /** Country code first, digits only — what the link and "copy number" use. */
  phoneDigits: z.string().regex(/^[1-9][0-9]{7,14}$/),
  url: z.string().max(6000),
  followUp: z.boolean(),
  /** `consentNotRecorded`: no WhatsApp consent is on file (none was withdrawn either). */
  warnings: z.array(z.enum(['consentNotRecorded', 'retryLaterNotReached'])),
});
export type PreparedContact = z.infer<typeof PreparedContactSchema>;

export const RecordContactOutcomeSchema = z
  .strictObject({
    outcome: z.enum(RECORDABLE_CONTACT_OUTCOMES),
    /** `sentManually` only: the person confirms they pressed Send themselves. */
    confirmSelfReported: z.literal(true).optional(),
    retryOn: BusinessDateSchema.optional(),
    note: z.string().trim().max(500).optional(),
  })
  .refine((value) => value.outcome !== 'sentManually' || value.confirmSelfReported === true, {
    message: 'SELF_REPORT_CONFIRMATION_REQUIRED',
    path: ['confirmSelfReported'],
  })
  .refine((value) => (value.outcome === 'retryLater') === (value.retryOn !== undefined), {
    message: 'RETRY_DATE_REQUIRED',
    path: ['retryOn'],
  });
export type RecordContactOutcome = z.infer<typeof RecordContactOutcomeSchema>;

export const ContactAttemptSchema = z.strictObject({
  attemptId: RecordIdSchema,
  /** The prepared attempt an outcome belongs to. */
  preparedAttemptId: RecordIdSchema.optional(),
  installmentId: RecordIdSchema,
  contractId: RecordIdSchema,
  customerId: RecordIdSchema,
  dueOn: BusinessDateSchema,
  bucket: ContactBucketSchema,
  channel: z.literal('whatsapp'),
  outcome: ContactOutcomeSchema,
  followUp: z.boolean(),
  reason: z.string().max(500).optional(),
  note: z.string().max(500).optional(),
  retryOn: BusinessDateSchema.optional(),
  language: LocaleSchema.optional(),
  phoneMasked: z.string().max(20).optional(),
  message: z.string().max(2000).optional(),
  /** True for `sentManually` and `contacted`: the person's own statement, not a provider's. */
  selfReported: z.boolean(),
  actorAccountId: z.string().min(1).max(200),
  at: InstantSchema,
});
export type ContactAttempt = z.infer<typeof ContactAttemptSchema>;

export const ContactHistoryQuerySchema = z
  .strictObject({
    installmentId: RecordIdSchema.optional(),
    contractId: RecordIdSchema.optional(),
    customerId: RecordIdSchema.optional(),
  })
  .refine((value) => Boolean(value.installmentId ?? value.contractId ?? value.customerId), {
    message: 'HISTORY_FILTER_REQUIRED',
  });
export type ContactHistoryQuery = z.infer<typeof ContactHistoryQuerySchema>;

export const ContactHistorySchema = z.strictObject({ items: z.array(ContactAttemptSchema) });

/** What the reminder centre states about sending (ADR-0043). */
export const ContactStatusSchema = z.strictObject({
  manual: z.strictObject({ available: z.literal(true), channel: z.literal('whatsapp') }),
  automatic: z.strictObject({
    available: z.literal(false),
    reason: z.literal('noProviderConfigured'),
  }),
  windowsDays: z.array(z.number().int()),
  templates: z.strictObject({ configured: z.boolean(), current: ReminderTemplatesSchema }),
  /**
   * The company WhatsApp number as configured in the company profile. Informational: a click-to-chat
   * link is sent by whichever account is open on the employee's device, never by this number.
   */
  companyAccount: z.strictObject({
    configured: z.boolean(),
    number: z.string().optional(),
    use: z.enum(['manualSending', 'contactOnly']).optional(),
  }),
  defaultCallingCode: z.string().optional(),
});
export type ContactStatus = z.infer<typeof ContactStatusSchema>;

export const CONTACT_AUDIT_ACTIONS = {
  prepared: 'collection.contact.prepared',
  outcomeRecorded: 'collection.contact.outcomeRecorded',
  refused: 'collection.contact.refused',
} as const;
